import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { isAdminRole, UserRole } from "../../app/userSlice";
import CoursePicker from "./picker/CoursePicker";
import DistrictPicker from "./picker/DistrictPicker";
import SchoolPicker from "./picker/SchoolPicker";

const leftNavActiveStudent = [
	{ name: "Home", href: "/home", current: false },
	{ name: "Writing", href: "/writing", current: false },
	{ name: "Reviews", href: "/reviews", current: false },
	{ name: "Lessons", href: "/lessons", current: false },
	{ name: "Reports", href: "/reports", current: false },
];

const leftNavActiveTeacher = [
	{ name: "Home", href: "/home", current: false },
	{ name: "Writing", href: "/writing", current: false },
	{ name: "Reviews", href: "/reviews", current: false },
	{ name: "Lessons", href: "/lessons", current: false },
	{ name: "Course", href: "/course", current: false },
	{ name: "Reports", href: "/reports", current: false },
];

const leftNavAdmin = [
	{ name: "Home", href: "/adminHome", current: false },
	{ name: "Users", href: "/users", current: false },
	{ name: "Prompts", href: "/prompts", current: false },
	{ name: "Reports", href: "/adminReports", current: false },
];

const leftNavInactive = [{ name: "Reports", href: "/reports", current: false }];

// rightNav is only used on the compact view
const rightNavWriterTeacher = [{ name: "Courses", href: "/courses", current: true }];
const rightNavSchoolAdmin = [{ name: "Schools", href: "/selectSchool", current: true }];
const rightNavDistrictAdmin = [{ name: "Districts", href: "/selectDistrict", current: true }];

interface Props {
	isCourseActive?: boolean;
	showLeftNav?: Boolean;
}

export default function CollapsibleNavbar(props: Props) {
	const { role, school, schools, districts } = useAppSelector((state) => state.user);

	const isWriter = role === UserRole.Writer;
	const isAdmin = isAdminRole(role);

	const leftNav = useMemo(() => {
		const baseNav = isAdmin
			? leftNavAdmin
			: props.isCourseActive
				? isWriter
					? leftNavActiveStudent
					: leftNavActiveTeacher
				: leftNavInactive;

		if (!isAdmin) {
			return baseNav;
		}

		let newTabsToAdd = [];

		if (role === UserRole.Admin || role === UserRole.StateAdmin) {
			newTabsToAdd.push({ name: "Districts", href: "/districts", current: false });
		}
		if (role !== UserRole.SchoolAdmin) {
			newTabsToAdd.push({ name: "Schools", href: "/schools", current: false });
		}
		if (role === UserRole.SchoolAdmin) {
			newTabsToAdd.push({
				name: "School",
				href: `/schools/edit?schoolId=${school?.id}`,
				current: false,
			});
		}

		let lastTabsToAdd = [];
		if (role === UserRole.Admin) {
			lastTabsToAdd.push({ name: "Alerts", href: "/alerts", current: false });
			lastTabsToAdd.push({ name: "Features", href: "/features", current: false });
			lastTabsToAdd.push({ name: "Demo", href: "/demo", current: false });
			lastTabsToAdd.push({ name: "Secrets", href: "/secrets", current: false });
		}

		return [...baseNav.slice(0, 1), ...newTabsToAdd, ...baseNav.slice(1), ...lastTabsToAdd];
	}, [isAdmin, isWriter, props.isCourseActive, role, school]);

	const rightNav = useMemo(() => {
		if (!isAdmin) {
			return rightNavWriterTeacher;
		}
		if (role === UserRole.SchoolAdmin) {
			return schools?.length === 1 ? undefined : rightNavSchoolAdmin;
		}
		if (role === UserRole.DistrictAdmin) {
			return districts?.length === 1 ? undefined : rightNavDistrictAdmin;
		}
		return undefined;
	}, [isAdmin, role, schools, districts]);

	// Only show picker for student, proctor or school and district admins.
	const getPicker = () => {
		const isDistrictAdmin = role === UserRole.DistrictAdmin;
		const isSchoolAdmin = role === UserRole.SchoolAdmin;
		return (
			<div className="min-[910px]:block hidden">
				<div className="flex space-x-4">
					<NavLink
						key={
							isDistrictAdmin
								? "DistrictPicker"
								: isSchoolAdmin
									? "SchoolPicker"
									: "CoursePicker"
						}
						to={
							isDistrictAdmin
								? "/districts"
								: isSchoolAdmin
									? "/selectSchool"
									: "/courses"
						}
						onClick={(e) => {
							e.preventDefault();
						}}>
						{isDistrictAdmin ? (
							<DistrictPicker />
						) : isSchoolAdmin ? (
							<SchoolPicker />
						) : (
							<CoursePicker />
						)}
					</NavLink>
				</div>
			</div>
		);
	};

	return (
		<Disclosure as="nav">
			{({ open }) => (
				<>
					<div className="mx-auto max-w-screen-2xl">
						<div className="flex items-center py-1.5 h-14">
							<div className="flex items-center justify-between w-full">
								<div className="min-[910px]:block hidden">
									{props.showLeftNav && (
										<div className="flex space-x-4">
											{leftNav.map((item) => (
												<NavLink
													key={item.name}
													to={item.href}
													className={({ isActive }) => `${
														isActive
															? "text-white font-bold underline underline-offset-4 decoration-1"
															: "text-indigo-100"
													}
															font-medium rounded-md bg-white/0 px-3 py-2 hover:bg-white/10`}
													aria-current={
														item.current ? "page" : undefined
													}>
													{item.name}
												</NavLink>
											))}
										</div>
									)}
								</div>
								{getPicker()}
							</div>

							<div className="min-[910px]:hidden -mr-2 flex">
								{/* Mobile menu button */}
								<DisclosureButton className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-white">
									<span className="sr-only">Open main menu</span>
									{open ? (
										<XMarkIcon className="block h-6 w-6" aria-hidden="true" />
									) : (
										<Bars3Icon className="block h-6 w-6" aria-hidden="true" />
									)}
								</DisclosureButton>
							</div>
						</div>
					</div>

					<DisclosurePanel className="min-[910px]:hidden">
						<div className="space-y-1 px-2 pt-2 pb-3">
							{props.showLeftNav && (
								<>
									{leftNav.map((item) => (
										<NavLink
											key={item.name}
											to={item.href}
											className={({ isActive }) =>
												`${
													isActive
														? "bg-primary-800 text-white"
														: "text-gray-300 hover:bg-primary-600 hover:text-white"
												}
													block rounded-md px-3 py-2 text-base font-medium no-underline`
											}>
											{item.name}
										</NavLink>
									))}
									{rightNav && <hr />}
								</>
							)}
							{rightNav &&
								rightNav?.map((item) => (
									<NavLink
										key={item.name}
										to={item.href}
										className={({ isActive }) =>
											`${
												isActive
													? "bg-primary-800 text-white"
													: "text-gray-300 hover:bg-primary-600 hover:text-white"
											}
													block rounded-md px-3 py-2 text-base font-medium no-underline`
										}>
										{item.name}
									</NavLink>
								))}
						</div>
					</DisclosurePanel>
				</>
			)}
		</Disclosure>
	);
}
